import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import * as RD from "@radix-ui/react-dialog";
import { Search } from "lucide-react";

export interface Command {
  id: string;
  label: string;
  group: string;
  hint?: string;
  keywords?: string;
  icon?: ComponentType<{ "aria-hidden"?: boolean | "true" | "false" }>;
  run: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: Command[];
  /** Busca assíncrona extra (ex.: pessoas pelo nome), com o texto digitado. */
  search?: (text: string) => Promise<Command[]>;
}

function norm(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function CommandPalette({ open, onOpenChange, commands, search }: CommandPaletteProps) {
  const [text, setText] = useState("");
  const [extra, setExtra] = useState<Command[]>([]);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) {
      setText("");
      setExtra([]);
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (!search || text.trim().length < 2) {
      setExtra([]);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      search(text.trim())
        .then((r) => alive && setExtra(r))
        .catch(() => alive && setExtra([]));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [text, search]);

  const filtered = useMemo(() => {
    const q = norm(text.trim());
    const base = q ? commands.filter((c) => norm(`${c.label} ${c.group} ${c.keywords ?? ""}`).includes(q)) : commands;
    return [...base.slice(0, 40), ...extra];
  }, [text, commands, extra]);

  useEffect(() => setActive(0), [text]);

  // Mantém o item ativo visível ao navegar com as setas.
  useEffect(() => {
    const id = filtered[active] ? `vx-cmd-${filtered[active]!.id}` : null;
    if (id) document.getElementById(id)?.scrollIntoView?.({ block: "nearest" });
  }, [active, filtered]);

  function run(c: Command | undefined) {
    if (!c) return;
    onOpenChange(false);
    c.run();
  }

  let lastGroup = "";
  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="vx-overlay" />
        <RD.Content className="vx-cmd" aria-describedby={undefined}>
          <RD.Title className="sr-only">Ir para</RD.Title>
          <div className="vx-cmd__search">
            <Search aria-hidden="true" />
            <input
              className="vx-cmd__input"
              autoFocus
              placeholder="Ir para tela, ação ou pessoa"
              value={text}
              role="combobox"
              aria-expanded="true"
              aria-controls="vx-cmd-list"
              aria-activedescendant={filtered[active] ? `vx-cmd-${filtered[active]!.id}` : undefined}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((a) => Math.min(a + 1, filtered.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((a) => Math.max(a - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  run(filtered[active]);
                }
              }}
            />
          </div>
          <ul className="vx-cmd__list" id="vx-cmd-list" role="listbox" ref={listRef} aria-label="Resultados">
            {filtered.length === 0 ? <li className="vx-cmd__empty">Nada encontrado para “{text.trim()}”.</li> : null}
            {filtered.map((c, i) => {
              const header = c.group !== lastGroup ? c.group : null;
              lastGroup = c.group;
              const Icon = c.icon;
              return (
                <li key={c.id} role="presentation">
                  {header ? (
                    <div className="vx-cmd__group" role="presentation">
                      {header}
                    </div>
                  ) : null}
                  <div
                    id={`vx-cmd-${c.id}`}
                    role="option"
                    aria-selected={i === active}
                    className="vx-cmd__item"
                    onMouseMove={() => setActive(i)}
                    onClick={() => run(c)}
                  >
                    {Icon ? <Icon aria-hidden="true" /> : null}
                    <span>{c.label}</span>
                    {c.hint ? <small>{c.hint}</small> : null}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="vx-cmd__foot" aria-hidden="true">
            <span>
              <kbd className="vx-kbd">↑</kbd>
              <kbd className="vx-kbd">↓</kbd> navegar
            </span>
            <span>
              <kbd className="vx-kbd">Enter</kbd> abrir
            </span>
            <span>
              <kbd className="vx-kbd">Esc</kbd> fechar
            </span>
            <span>pessoas: digite 2 letras do nome</span>
          </div>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
